import { motion, useInView } from "framer-motion";
import { ArrowRight, Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { useLanguage } from "../contexts/languageStore";
import { useWebsite } from "../contexts/websiteStore";

interface ContactFormData {
	name: string;
	email: string;
	phone?: string;
	company?: string;
	message: string;
}

export function Contact() {
	const { t, isRTL } = useLanguage();
	const { config } = useWebsite();
	const ref = useRef<HTMLDivElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-50px" });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState<ContactFormData>({
		name: "",
		email: "",
		phone: "",
		company: "",
		message: "",
	});
	const [errors, setErrors] = useState<Partial<ContactFormData>>({});

	const validateForm = (): boolean => {
		const newErrors: Partial<ContactFormData> = {};

		if (!formData.name || formData.name.length < 2) {
			newErrors.name = t("contact.form.errors.name");
		}

		if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = t("contact.form.errors.email");
		}

		if (!formData.message || formData.message.length < 10) {
			newErrors.message = t("contact.form.errors.message");
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (field: keyof ContactFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Simulate form submission
			await new Promise((resolve) => setTimeout(resolve, 1500));

			// Show success message
			alert(t("contact.form.success"));

			// Reset form
			setFormData({
				name: "",
				email: "",
				phone: "",
				company: "",
				message: "",
			});
			setErrors({});
		} catch (error) {
			alert(t("contact.form.error"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const contactInfo = [
		{
			icon: Phone,
			title: t("contact.info.phone.title"),
			detail:
				config.name === "EMCOR" ? (
					<div className="space-y-1">
						<div className="flex items-center">
							<span className="text-xs font-medium text-gray-500 mr-2">
								UAE:
							</span>
							<span>{t("contact.info.phone.uae")}</span>
						</div>
						<div className="flex items-center">
							<span className="text-xs font-medium text-gray-500 mr-2">
								Switzerland:
							</span>
							<span>{t("contact.info.phone.switzerland")}</span>
						</div>
					</div>
				) : (
					<span style={{ direction: "ltr", unicodeBidi: "embed" }}>
						{t("contact.info.phone.number")}
					</span>
				),
			color: "primary",
		},
		{
			icon: MapPin,
			title: t("contact.info.address.title"),
			detail: t("contact.info.address.description"),
			color: "primary",
		},
		{
			icon: Clock,
			title: t("contact.info.hours.title"),
			detail: t("contact.info.hours.timing"),
			color: "primary",
		},
		{
			icon: Mail,
			title: t("contact.info.email.title"),
			detail: t("contact.info.email.address"),
			color: "primary",
		},
	];

	return (
		<section id="contact" className="mt-20 relative overflow-hidden mb-20">
			{/* Interactive gradient background */}
			<motion.div
				className="absolute inset-0"
				animate={{
					background: [
						`radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 40%)`,
						`radial-gradient(circle at 50% 50%, var(--secondary-color-rgb, 34, 197, 94) / 0.12 0%, transparent 40%)`,
						`radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 40%)`,
					],
				}}
				transition={{
					duration: 0.8,
					ease: "easeOut",
				}}
			/>

			{/* Enhanced background with vibrant gradients */}
			<div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-primary/5 to-secondary/5"></div>
			<div className="absolute inset-0 bg-gradient-to-r from-primary/3 via-transparent to-secondary/3"></div>

			{/* Animated background elements */}
			<motion.div
				className="absolute top-0 left-0 w-full h-full"
				animate={{
					background: [
						`radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.08 0%, transparent 50%)`,
						`radial-gradient(circle at 80% 20%, var(--secondary-color-rgb, 34, 197, 94) / 0.08 0%, transparent 50%)`,
						`radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.08 0%, transparent 50%)`,
					],
				}}
				transition={{
					duration: 8,
					repeat: Infinity,
					ease: "easeInOut",
				}}
			/>

			<div className="container mx-auto px-6 relative z-10 max-w-7xl">
				<motion.div
					ref={ref}
					initial={{ opacity: 0, y: 30 }}
					animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
					transition={{ duration: 1.2, ease: "easeOut" }}
					className="text-center mb-16"
				>
					<motion.div
						className="inline-flex items-center px-8 py-4 bg-primary/5 text-primary border border-primary/10 text-sm font-light tracking-wider mb-8 mt-10"
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ delay: 0.2, duration: 0.8 }}
					>
						<span className="w-px h-4 bg-gray-800 mr-4"></span>
						<span className="uppercase tracking-[0.2em]">
							{t("contact.section.header")}
						</span>
						<span className="w-px h-4 bg-gray-800 ml-4"></span>
					</motion.div>
					<motion.h2
						className="text-4xl md:text-6xl font-light text-gray-900 mb-8 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
						transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
					>
						{t("contact.heading.title")}
					</motion.h2>
					<motion.p
						className="text-lg text-gray-600 max-w-3xl mx-auto tracking-wide"
						initial={{ opacity: 0, y: 30 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
						transition={{ delay: 0.5, duration: 1.2, ease: "easeOut" }}
					>
						{t("contact.heading.subtitle")}
					</motion.p>
				</motion.div>

				<div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
					{/* Contact Form */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="lg:col-span-2"
					>
						<div className="bg-gradient-to-br from-white/90 to-primary/5 backdrop-blur-sm rounded-2xl shadow-lg border border-primary/20 p-8">
							<h3 className="text-xl font-semibold text-gray-900 mb-8">
								{t("contact.form.title")}
							</h3>
							<form onSubmit={handleSubmit} className="space-y-6">
								{/* Name */}
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={
										isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
									}
									transition={{ duration: 0.6, delay: 0.3 }}
								>
									<label
										htmlFor="name"
										className="block text-sm font-medium text-gray-700 mb-2"
									>
										{t("contact.form.name")}
									</label>
									<input
										id="name"
										type="text"
										value={formData.name}
										onChange={(e) => handleInputChange("name", e.target.value)}
										className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 bg-white/70 backdrop-blur-sm ${
											errors.name
												? "border-red-500"
												: "border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/20"
										}`}
										placeholder={t("contact.form.name")}
									/>
									{errors.name && (
										<p className="text-red-500 text-sm mt-1">{errors.name}</p>
									)}
								</motion.div>

								{/* Email */}
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={
										isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
									}
									transition={{ duration: 0.6, delay: 0.4 }}
								>
									<label
										htmlFor="email"
										className="block text-sm font-medium text-gray-700 mb-2"
									>
										{t("contact.form.email")}
									</label>
									<input
										id="email"
										type="email"
										value={formData.email}
										onChange={(e) => handleInputChange("email", e.target.value)}
										className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 bg-white/70 backdrop-blur-sm ${
											errors.email
												? "border-red-500"
												: "border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/20"
										}`}
										placeholder={t("contact.form.email")}
									/>
									{errors.email && (
										<p className="text-red-500 text-sm mt-1">{errors.email}</p>
									)}
								</motion.div>

								{/* Phone & Company */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										animate={
											isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
										}
										transition={{ duration: 0.6, delay: 0.45 }}
									>
										<label
											htmlFor="phone"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											{t("contact.form.phone")}
										</label>
										<input
											id="phone"
											type="tel"
											value={formData.phone}
											onChange={(e) =>
												handleInputChange("phone", e.target.value)
											}
											className="w-full px-4 py-3 border border-primary/30 rounded-lg transition-all duration-300 bg-white/70 backdrop-blur-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
											placeholder={t("contact.form.phone")}
										/>
									</motion.div>

									<motion.div
										initial={{ opacity: 0, y: 20 }}
										animate={
											isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
										}
										transition={{ duration: 0.6, delay: 0.5 }}
									>
										<label
											htmlFor="company"
											className="block text-sm font-medium text-gray-700 mb-2"
										>
											{t("contact.form.company")}
										</label>
										<input
											id="company"
											type="text"
											value={formData.company}
											onChange={(e) =>
												handleInputChange("company", e.target.value)
											}
											className="w-full px-4 py-3 border border-primary/30 rounded-lg transition-all duration-300 bg-white/70 backdrop-blur-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
											placeholder={t("contact.form.company")}
										/>
									</motion.div>
								</div>

								{/* Message */}
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={
										isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
									}
									transition={{ duration: 0.6, delay: 0.55 }}
								>
									<label
										htmlFor="message"
										className="block text-sm font-medium text-gray-700 mb-2"
									>
										{t("contact.form.message")}
									</label>
									<textarea
										id="message"
										rows={6}
										value={formData.message}
										onChange={(e) =>
											handleInputChange("message", e.target.value)
										}
										className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 resize-none bg-white/70 backdrop-blur-sm ${
											errors.message
												? "border-red-500"
												: "border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/20"
										}`}
										placeholder={t("contact.form.message")}
									/>
									{errors.message && (
										<p className="text-red-500 text-sm mt-1">
											{errors.message}
										</p>
									)}
								</motion.div>

								{/* Submit Button */}
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={
										isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
									}
									transition={{ duration: 0.6, delay: 0.6 }}
								>
									<button
										type="submit"
										disabled={isSubmitting}
										className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center gap-2"
									>
										{isSubmitting ? (
											<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
										) : (
											<>
												{t("contact.form.send")}
												<Send className="h-5 w-5" />
											</>
										)}
									</button>
								</motion.div>
							</form>
						</div>
					</motion.div>

					{/* Contact Info */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
						transition={{ duration: 0.8, delay: 0.3 }}
						className="space-y-8"
					>
						<div className="bg-gradient-to-br from-white/90 to-secondary/5 backdrop-blur-sm rounded-2xl shadow-lg border border-secondary/20 p-8">
							<div className="space-y-8">
								{contactInfo.map((info, idx) => (
									<motion.div
										key={idx}
										initial={{ opacity: 0, y: 20 }}
										animate={
											isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
										}
										transition={{ duration: 0.6, delay: 0.4 + idx * 0.1 }}
										className="flex items-start gap-4"
									>
										<info.icon className="w-6 h-6 mt-1 text-primary" />
										<div>
											<div className="text-base font-medium text-gray-900 mb-1">
												{info.title}
											</div>
											<div className="text-sm text-gray-600">{info.detail}</div>
										</div>
									</motion.div>
								))}
							</div>

							{/* Office Location */}
							<div className="mt-10">
								<div className="flex items-start mb-4 gap-4">
									<MapPin className="w-6 h-6 mt-1 text-primary" />
									<div className="text-base font-medium text-gray-900">
										{t("contact.office.location")}
									</div>
								</div>
								<div className="text-sm text-gray-600 mb-4 ml-10">
									{t("contact.info.office.address")}
								</div>

							<div className="rounded-lg overflow-hidden border border-gray-300 mt-6">
								{config.name === "EMCOR" ? (
									<iframe
										src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1234567890123!2d54.3773!3d24.4539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDI3JzE0LjAiTiA1NMKwMjInMzguMyJF!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae&q=${encodeURIComponent(t("contact.info.address.description"))}`}
										width="100%"
										height="200"
										style={{ border: 0 }}
										allowFullScreen
										loading="lazy"
										referrerPolicy="no-referrer-when-downgrade"
										title="Office Location Map"
									/>
								) : (
									<iframe
										src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1234567890123!2d55.2599559!3d25.184313!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6900458ae623%3A0x5ff6b24e9db43266!2sAl%20Manara%20Tower!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae"
										width="100%"
										height="200"
										style={{ border: 0 }}
										allowFullScreen
										loading="lazy"
										referrerPolicy="no-referrer-when-downgrade"
										title="Office Location Map"
									/>
								)}
							</div>
							</div>
						</div>

						{/* Additional CTA */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
							transition={{ duration: 0.6, delay: 0.9 }}
						>
							<div className="bg-gradient-to-br from-primary via-primary/90 to-secondary/80 rounded-2xl p-8 text-center text-white shadow-xl border border-primary/20">
								<h4 className="text-2xl text-primary font-medium mb-4">
									{t("contact.consultation.title")}
								</h4>
								<p className="text-black text-sm mb-6">
									{t("contact.consultation.description")}
								</p>
								<button className="bg-primary text-white hover:bg-primary/50 px-6 py-3 rounded-lg font-medium transition-all duration-300">
									{t("contact.consultation.button")}
								</button>
							</div>
						</motion.div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
